import React, { forwardRef, useState, useEffect } from 'react';
import './AlgorithmCard.css';

const AlgorithmCard = forwardRef(({ data, isExpanded, onToggle, type }, ref) => {
  const [chartData, setChartData] = useState({ daily: null, minute: null });
  const [chartLoading, setChartLoading] = useState(false);

  // 차트 데이터 로드
  useEffect(() => {
    if (isExpanded && !chartData.daily && !chartData.minute) {
      loadChartData();
    }
  }, [isExpanded]);

  const loadChartData = async () => {
    setChartLoading(true);
    try {
      // 실제로는 차트 API를 호출해야 함
      const mockChartData = await mockChartApiCall(data.stockCode);
      setChartData(mockChartData);
    } catch (error) {
      console.error('차트 데이터 로드 실패:', error);
    } finally {
      setChartLoading(false);
    }
  };

  // 등락률에 따른 색상 결정
  const getChangeColor = (diff) => {
    if (diff > 0) return 'positive';
    if (diff < 0) return 'negative';
    return 'neutral';
  };

  // 숫자 포맷팅 함수
  const formatNumber = (num) => {
    if (num >= 1000000000000) {
      return (num / 1000000000000).toFixed(1) + '조';
    }
    if (num >= 100000000) {
      return (num / 100000000).toFixed(1) + '억';
    }
    if (num >= 10000) {
      return (num / 10000).toFixed(1) + '만';
    }
    return num.toLocaleString();
  };

  const formatPrice = (price) => {
    return price.toLocaleString() + '원';
  };

  const formatChangeRate = (diff) => {
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff.toFixed(2)}%`;
  };

  return (
    <div ref={ref} className={`algorithm-card ${isExpanded ? 'expanded' : ''}`}>
      {/* 카드 헤더 - 모든 정보를 한줄로 표시 */}
      <div className="card-header" onClick={onToggle}>
        <div className="stock-info">
          <h3 className="stock-name">{data.stockName}</h3>
          <span className="stock-code">({data.stockCode})</span>
        </div>

        <div className="price-info">
          <span className="stock-price">{formatPrice(data.price)}</span>
          <span className={`change-rate ${getChangeColor(data.diff)}`}>
            {formatChangeRate(data.diff)}
          </span>
        </div>

        <div className="algorithm-summary">
          <span className="summary-item">
            <span className="summary-label">순위:</span>
            <span className="summary-value">{data.rank}위</span>
          </span>
          {data.nullim_days && (
            <span className="summary-item">
              <span className="summary-label">눌림일:</span>
              <span className="summary-value">{data.nullim_days}일</span>
            </span>
          )}
          <span className="summary-item">
            <span className="summary-label">메시지:</span>
            <span className="summary-message">{data.message}</span>
          </span>
        </div>

        <div className="expand-icon">
          {isExpanded ? '▼' : '▶'}
        </div>
      </div>

      {/* 확장 영역 - 차트 */}
      {isExpanded && (
        <div className="card-expanded">
          <div className="chart-section">
            <h4 className="chart-title">차트 분석</h4>

            {chartLoading ? (
              <div className="chart-loading">
                <div className="loading-spinner">⏳</div>
                <p>차트를 로드하는 중...</p>
              </div>
            ) : (
              <div className="charts-container">
                <div className="chart-item">
                  <h5 className="chart-subtitle">일봉 차트 (40일)</h5>
                  <div className="chart-placeholder daily-chart">
                    {chartData.daily ? (
                      <div className="mock-chart">
                        <div className="chart-bars">
                          {chartData.daily.map((item, index) => (
                            <div
                              key={index}
                              className={`chart-bar ${item.change > 0 ? 'positive' : 'negative'}`}
                              style={{ height: `${Math.abs(item.change) * 5 + 20}px` }}
                            />
                          ))}
                        </div>
                        <div className="chart-info">
                          <span>최근 40일 일봉 데이터</span>
                        </div>
                      </div>
                    ) : (
                      <div className="chart-error">일봉 차트 로드 실패</div>
                    )}
                  </div>
                </div>

                <div className="chart-item">
                  <h5 className="chart-subtitle">분봉 차트 (380분)</h5>
                  <div className="chart-placeholder minute-chart">
                    {chartData.minute ? (
                      <div className="mock-chart">
                        <div className="chart-bars">
                          {chartData.minute.slice(0, 50).map((item, index) => (
                            <div
                              key={index}
                              className={`chart-bar ${item.change > 0 ? 'positive' : 'negative'}`}
                              style={{ height: `${Math.abs(item.change) * 3 + 15}px` }}
                            />
                          ))}
                        </div>
                        <div className="chart-info">
                          <span>최근 380분 분봉 데이터</span>
                        </div>
                      </div>
                    ) : (
                      <div className="chart-error">분봉 차트 로드 실패</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 추가 분석 정보 */}
          <div className="analysis-section">
            <h4 className="analysis-title">AI 분석 결과</h4>
            <div className="analysis-content">
              <div className="analysis-item">
                <span className="analysis-label">패턴 신뢰도:</span>
                <span className="analysis-value">
                  {Math.floor(Math.random() * 30) + 70}%
                </span>
              </div>
              <div className="analysis-item">
                <span className="analysis-label">분석 유형:</span>
                <span className="analysis-value">
                  {type === 'pressed' ? '눌림 패턴' : '테마 분석'}
                </span>
              </div>
              <div className="analysis-item">
                <span className="analysis-label">리스크 레벨:</span>
                <span className="analysis-value">
                  {['낮음', '보통', '높음'][Math.floor(Math.random() * 3)]}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// Mock 차트 API 호출 함수 (실제 구현 시 교체 필요)
const mockChartApiCall = async (stockCode) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const daily = Array.from({ length: 40 }, (_, i) => ({
        date: new Date(Date.now() - (39 - i) * 24 * 60 * 60 * 1000),
        change: (Math.random() - 0.5) * 10
      }));

      const minute = Array.from({ length: 380 }, (_, i) => ({
        time: new Date(Date.now() - (379 - i) * 60 * 1000),
        change: (Math.random() - 0.5) * 5
      }));

      resolve({ daily, minute });
    }, 800);
  });
};

AlgorithmCard.displayName = 'AlgorithmCard';

export default AlgorithmCard;